import { FC, useState, useRef } from 'react';
import { IconChevronDown } from '@maverick/icons/dist/IconChevronDown';
import { IconChevronUp } from '@maverick/icons/dist/IconChevronUp';
import styles from './Dropdown.module.scss';
import { Icon } from '../Icon';
import { useOnOutsideClick } from '../../../shared/hooks/useOnOutsideClick';

export enum DropdownOptions {
	All = 'All',
	Rewards = 'Rewards',
	Visits = 'Visits',
}

export interface DropdownProps {
	items: Array<string>;
	onClickOption: (option: string) => void;
	selected: string;
	disabled?: boolean;
}

export const Dropdown: FC<DropdownProps> = ({ items, selected, onClickOption, disabled = false }) => {
	const [isOpen, setIsOpen] = useState<boolean>(false);
	const customDropdown = useRef<HTMLDivElement>(null);

	useOnOutsideClick(customDropdown, () => {
		setIsOpen(false);
	});

	const handleClickOption = (item: string) => {
		setIsOpen(false);
		onClickOption(item);
	};

	const handleKeyDown = (ev: React.KeyboardEvent, index: number, item?: string) => {
		switch (ev.key) {
			case ' ':
			case 'Enter':
				ev.preventDefault();
				if (item) {
					onClickOption(item);
				}
				setIsOpen(!isOpen);
				break;
			case 'ArrowUp':
				ev.preventDefault();
				if (index > 0) {
					const element = document.getElementById(`dropdown-option-${index - 1}`);
					element?.focus();
				}
				break;
			case 'ArrowDown':
				ev.preventDefault();
				const element = document.getElementById(`dropdown-option-${index + 1}`);
				element?.focus();
				break;
			case 'Escape':
				setIsOpen(!isOpen);
				break;
			default:
				break;
		}
	};

	return (
		<div data-testid='dropdown-container' className={styles['container']} ref={customDropdown} tabIndex={-1}>
			<div
				className={!disabled ? styles['selected'] : styles['selectedDisabled']}
				tabIndex={0}
				onClick={() => setIsOpen(!isOpen)}
				onKeyDown={(ev) => handleKeyDown(ev, -1)}
				id='selected-option'
				data-testid='selected-option'
			>
				{selected}
				<div className={styles['chevron']}>
					{!disabled && <Icon icon={isOpen ? IconChevronUp : IconChevronDown} />}
				</div>
			</div>

			{!disabled && isOpen && (
				<div data-testid='dropdown-options-container' className={styles['options']}>
					{items.map((item, i) => (
						<div
							className={styles['option']}
							id={`dropdown-option-${i}`}
							data-testid={`dropdown-option-${i}`}
							key={i}
							onClick={() => {
								handleClickOption(item);
							}}
							onKeyDown={(ev) => handleKeyDown(ev, i, item)}
							tabIndex={0}
							aria-label={`Dropdown Option - ${item}`}
						>
							<span className={styles['optionText']}>{item}</span>
						</div>
					))}
				</div>
			)}
		</div>
	);
};
